/**
 * formatDateTime.ts — locale-aware timestamp formatting for the Leads panel.
 *
 * Operators read `created_at` / `status_updated_at` (ISO-8601 from the
 * contract). Formatting goes through `Intl.DateTimeFormat` bound to the active
 * i18n locale — never a hand-rolled, locale-less string (working-principle:
 * i18n from day one). An unparseable / empty timestamp degrades to "—" rather
 * than rendering "Invalid Date".
 */

export const formatDateTime = (iso: string | null | undefined, locale: string): string => {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(ms));
};

/**
 * Date-only, locale-aware formatting for a `YYYY-MM-DD` filter value (no time).
 * Used to render date-filter chips in the operator's locale (pt-BR → dd/mm/yyyy)
 * instead of the browser's default US order. Parsed as a local calendar date so
 * a `YYYY-MM-DD` value never shifts a day across a timezone offset.
 */
export const formatDateOnly = (value: string | null | undefined, locale: string): string => {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(Date.parse(value));
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(date);
};
